import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MagnifyingGlass, UserPlus, DeviceMobile, CheckCircle, ArrowCounterClockwise, LockSimple, LockSimpleOpen, Plus, X, CaretRight, CaretLeft, Receipt, Barcode, WhatsappLogo, Printer, CurrencyEur, Package, Trash, PencilSimple, Archive, DotsThree, CalendarPlus, CalendarCheck } from "@phosphor-icons/react";
import { printRepairReceipt, printRepairLabel, printRepairClosure } from "@/lib/printRepair";
import { whatsappRepairReady, whatsappContactClient } from "@/lib/whatsapp";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";
const overlineCls = "text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3";

const ACCESSORY_SUGGESTIONS = ["Cover", "Cavo di alimentazione", "Alimentatore", "Caricabatterie", "Borsa", "Custodia", "Auricolari", "Scheda SIM", "Scheda SD"];
const PAGE_SIZE = 50;
const RESOLUTION_SUGGESTIONS = [
  "Sostituzione Display",
  "Sostituzione Batteria",
  "Sostituzione Fotocamera Anteriore",
  "Sostituzione Fotocamera Posteriore",
  "Sostituzione parte posteriore",
  "Riparazione su scheda madre",
  "Formattazione",
  "Formattazione e salvataggio dati",
];

const parseUtc = (d) => {
  const iso = typeof d === "string" && !d.endsWith("Z") && !d.includes("+") ? `${d}Z` : d;
  return new Date(iso);
};
const fmtDate = (d) => { const p = parseUtc(d); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const normSubs = (subs) => (subs || []).map((s) => (typeof s === "string" ? { name: s, models: [] } : s));

const dedupeWords = (s) => (s || "").trim().split(/\s+/).filter((w, i, arr) => i === 0 || arr[i - 1].toLowerCase() !== w.toLowerCase()).join(" ");

const deviceDisplay = (r) => {
  const label = dedupeWords(r.device_label);
  const low = label.toLowerCase();
  const sub = r.subcategory && !low.includes(r.subcategory.toLowerCase()) ? ` · ${r.subcategory}` : "";
  const type = r.device_type && !low.includes(r.device_type.toLowerCase()) ? ` (${r.device_type})` : "";
  return { label, sub, type };
};

function ClientSearch({ onSelect, selected }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const debounce = useRef(null);

  useEffect(() => {
    clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setOpen(false); setCreating(false); return; }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients/search", { params: { phone: q, name: q } });
        setResults(data);
        setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [query]);

  const startCreate = () => {
    const q = query.trim();
    const digits = q.replace(/\D/g, "");
    if (digits.length >= 3 && !/[a-zA-Z]/.test(q)) {
      setNewPhone(q);
      setNewName("");
    } else {
      setNewName(q);
      setNewPhone("");
    }
    setCreating(true);
  };

  const createClient = async () => {
    if (!newName.trim() || !newPhone.trim()) {
      toast.error("Inserisci nome e numero di telefono");
      return;
    }
    try {
      const { data } = await api.post("/clients", { name: newName.trim(), phone: newPhone.trim() });
      toast.success("Cliente creato");
      onSelect(data);
      setOpen(false);
      setCreating(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (selected) {
    return (
      <div className="flex items-center justify-between border border-primary/50 bg-primary/10 rounded-md px-4 py-3" data-testid="selected-client">
        <div>
          <p className="text-sm font-bold">{selected.name}</p>
          <p className="text-xs text-muted-foreground">{selected.phone}{selected.phone2 ? ` · ${selected.phone2}` : ""}</p>
        </div>
        <button type="button" onClick={() => { onSelect(null); setQuery(""); setCreating(false); }} data-testid="change-client-button"
          className="text-xs font-semibold text-primary hover:opacity-80 transition-opacity duration-150">
          Cambia
        </button>
      </div>
    );
  }

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div className="relative">
      <label className={labelCls}>Cliente (nome, cognome o numero di telefono)</label>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          data-testid="client-search-input" className={`${inputCls} pl-10`} placeholder="Es. Rossi oppure 338 1234567" />
      </div>
      {showDropdown && (
        <div className="mt-2 w-full max-h-96 overflow-y-auto bg-popover border border-border rounded-md" data-testid="client-search-results">
          {results.map((c) => (
            <button key={c.id} type="button"
              onClick={() => { onSelect(c); setOpen(false); }}
              data-testid={`client-result-${c.id}`}
              className="w-full text-left px-4 py-3 hover:bg-accent transition-colors duration-150 flex justify-between items-center border-b border-border/50 last:border-b-0">
              <span className="text-sm font-semibold">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.phone}</span>
            </button>
          ))}
          {creating ? (
            <div className="px-4 py-3.5 border-t border-border space-y-2.5 bg-accent/30">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Nuovo cliente</p>
              <input value={newName} onChange={(e) => setNewName(e.target.value.replace(/\d/g, ""))} placeholder="Nome e cognome *"
                data-testid="new-client-name-input" className={inputCls} />
              <input value={newPhone} inputMode="tel" onChange={(e) => setNewPhone(e.target.value.replace(/[^\d+]/g, ""))} placeholder="Numero di telefono *"
                data-testid="new-client-phone-input" className={inputCls} />
              <button type="button" onClick={createClient} data-testid="confirm-new-client-button"
                className="w-full py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
                Crea cliente
              </button>
            </div>
          ) : (
            <button type="button" onClick={startCreate} data-testid="create-new-client-button"
              className="w-full px-4 py-3 border-t border-border text-left text-sm font-semibold text-primary hover:bg-accent transition-colors duration-150 inline-flex items-center gap-2 sticky bottom-0 bg-popover">
              <UserPlus size={15} /> Aggiungi cliente
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CascadeColumn({ title, items, selected, onSelect, itemTestid, adding, setAdding, newName, setNewName, onAdd, addTestid, inputTestid, confirmTestid, placeholder, onDelete }) {
  return (
    <div className="border border-border rounded-md overflow-hidden flex flex-col bg-background">
      <p className="px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border bg-accent/40 font-semibold">{title}</p>
      <div className="h-72 overflow-y-auto divide-y divide-border">
        {items.length === 0 && (
          <p className="px-3 py-8 text-xs text-muted-foreground text-center">{placeholder}</p>
        )}
        {items.map((item) => (
          <div key={item} className={`w-full flex items-center transition-colors duration-150 ${
              selected === item ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-accent"}`}>
            <button type="button" onClick={() => onSelect(item)}
              data-testid={`${itemTestid}-${slug(item)}`}
              className="flex-1 min-w-0 px-3.5 py-2.5 text-left text-sm flex items-center justify-between">
              <span className="truncate">{item}</span>
              <CaretRight size={13} className={selected === item ? "" : "text-muted-foreground"} />
            </button>
            {onDelete && (
              <button type="button" onClick={() => onDelete(item)} data-testid={`${itemTestid}-delete-${slug(item)}`}
                title="Elimina voce"
                className={`px-2.5 shrink-0 transition-colors duration-150 ${selected === item ? "text-primary-foreground/70 hover:text-primary-foreground" : "text-muted-foreground/50 hover:text-destructive"}`}>
                <Trash size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      {adding ? (
        <div className="p-2 border-t border-border flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
            placeholder="Nome..." data-testid={inputTestid} className={`${inputCls} py-2`} />
          <button type="button" onClick={onAdd} data-testid={confirmTestid}
            className="px-3 rounded-md bg-primary text-primary-foreground shrink-0 hover:opacity-90 transition-opacity duration-150">
            <Plus size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid={addTestid}
          className="w-full px-3.5 py-2.5 text-left text-xs font-semibold text-primary border-t border-border hover:bg-accent inline-flex items-center gap-1.5 transition-colors duration-150">
          <Plus size={13} weight="bold" /> Aggiungi
        </button>
      )}
    </div>
  );
}

export function EditRepairDialog({ repair, open, onOpenChange, technicians, onSaved }) {
  const [problem, setProblem] = useState("");
  const [accessories, setAccessories] = useState([]);
  const [unlockPwd, setUnlockPwd] = useState("");
  const [noPassword, setNoPassword] = useState(false);
  const [price, setPrice] = useState("");
  const [estDelivery, setEstDelivery] = useState("");
  const [techId, setTechId] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && repair) {
      setProblem(repair.problem || "");
      setAccessories(repair.accessories || []);
      setUnlockPwd(repair.unlock_password || "");
      setNoPassword(!!repair.no_password);
      setPrice(repair.price != null ? String(repair.price).replace(".", ",") : "");
      setEstDelivery(repair.estimated_delivery || "");
      setTechId(repair.technician_id || "");
      setInternalNotes(repair.internal_notes || "");
      setNotes(repair.notes || "");
      setBrand(""); setModel(""); setSerial("");
      if (repair.device_id && repair.client_id) {
        api.get(`/clients/${repair.client_id}/devices`).then((r) => {
          const dev = (r.data || []).find((d) => d.id === repair.device_id);
          if (dev) {
            setBrand(dev.brand || "");
            setModel(dev.model || "");
            setSerial(dev.serial || "");
          }
        }).catch(() => {});
      }
    }
  }, [open, repair]);

  const toggleAcc = (a) => setAccessories((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const save = async () => {
    if (!problem.trim()) { toast.error("Descrivi il problema"); return; }
    setSaving(true);
    try {
      const priceVal = parseFloat(String(price).replace(",", "."));
      await api.patch(`/repairs/${repair.id}`, {
        problem: problem.trim(),
        accessories,
        unlock_password: noPassword ? null : (unlockPwd.trim() || null),
        no_password: noPassword,
        price: price !== "" && !isNaN(priceVal) ? priceVal : undefined,
        estimated_delivery: estDelivery || undefined,
        internal_notes: internalNotes,
        notes: notes,
        technician_id: techId || undefined,
        brand: brand.trim() || undefined,
        model: model.trim() || undefined,
        serial: serial.trim(),
      });
      toast.success("Pratica aggiornata");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!repair) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Modifica pratica — {repair.client_name}</DialogTitle>
          <DialogDescription className="sr-only">Modifica i dati della riparazione in lavorazione</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Marca</label>
              <input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="edit-repair-brand" className={inputCls} placeholder={repair.device_label} />
            </div>
            <div>
              <label className={labelCls}>Modello</label>
              <input value={model} onChange={(e) => setModel(e.target.value)} data-testid="edit-repair-model" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Seriale / IMEI</label>
            <input value={serial} onChange={(e) => setSerial(e.target.value)} data-testid="edit-repair-serial" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Problema *</label>
            <textarea value={problem} onChange={(e) => setProblem(e.target.value)} rows={2} data-testid="edit-repair-problem" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Accessori lasciati</label>
            <div className="flex flex-wrap gap-2">
              {ACCESSORY_SUGGESTIONS.map((a) => (
                <button key={a} type="button" onClick={() => toggleAcc(a)} data-testid={`edit-accessory-chip-${slug(a)}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                    accessories.includes(a) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Password / PIN sblocco</label>
              <input value={unlockPwd} onChange={(e) => setUnlockPwd(e.target.value)} disabled={noPassword} data-testid="edit-repair-password" className={inputCls} />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={noPassword} onChange={(e) => setNoPassword(e.target.checked)} data-testid="edit-repair-no-password" className="w-4 h-4 accent-primary" />
                Senza password
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Prezzo (€)</label>
              <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" data-testid="edit-repair-price" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Consegna stimata</label>
              <input type="date" value={estDelivery} onChange={(e) => setEstDelivery(e.target.value)} data-testid="edit-repair-delivery" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Tecnico assegnato</label>
            <select value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="edit-repair-technician" className={inputCls}>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Note</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="edit-repair-notes" className={inputCls} placeholder="Note sulla pratica..." />
          </div>
          <div>
            <label className={labelCls}>Note interne</label>
            <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2} data-testid="edit-repair-internal-notes" className={inputCls} placeholder="Annotazioni visibili solo allo staff..." />
          </div>
          <button onClick={save} disabled={saving} data-testid="confirm-edit-repair-button"
            className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RepairDialog({ open, onOpenChange, onCreated, technicians, initialClient, initialProblem, initialTechnicianId, initialPrice, initialOrderId }) {
  const [client, setClient] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [newDevice, setNewDevice] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catId, setCatId] = useState(null);
  const [sub, setSub] = useState("");
  const [modelSel, setModelSel] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [addingSub, setAddingSub] = useState(false);
  const [newSubName, setNewSubName] = useState("");
  const [addingModel, setAddingModel] = useState(false);
  const [newModelName, setNewModelName] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [accessories, setAccessories] = useState([]);
  const [customAcc, setCustomAcc] = useState("");
  const [problem, setProblem] = useState("");
  const [unlockPwd, setUnlockPwd] = useState("");
  const [noPassword, setNoPassword] = useState(false);
  const [techId, setTechId] = useState("");
  const [price, setPrice] = useState("");
  const [estDelivery, setEstDelivery] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (open && technicians.length && !techId) {
      if (initialTechnicianId && technicians.some((t) => t.id === initialTechnicianId)) setTechId(initialTechnicianId);
      else {
        const cocca = technicians.find((t) => /cocca/i.test(t.name));
        setTechId((cocca || technicians[0]).id);
      }
    }
  }, [open, technicians]);

  useEffect(() => {
    if (open) {
      setCreated(null);
      api.get("/device-categories").then((r) => setCategories(r.data)).catch(() => {});
      if (technicians.length && !techId) {
        const cocca = technicians.find((t) => /cocca/i.test(t.name));
        setTechId((cocca || technicians[0]).id);
      }
      if (initialClient) setClient(initialClient);
      if (initialProblem) setProblem(initialProblem);
      if (initialPrice != null && initialPrice !== "") setPrice(String(initialPrice).replace(".", ","));
    } else {
      setClient(null); setDevices([]); setDeviceId(null); setNewDevice(false);
      setTechId("");
      setCatId(null); setSub(""); setModelSel(""); setBrand(""); setModel(""); setSerial("");
      setAccessories([]); setCustomAcc(""); setProblem(""); setUnlockPwd("");
      setNoPassword(false); setPrice(""); setEstDelivery(""); setNotes("");
      setAddingCat(false); setAddingSub(false); setAddingModel(false);
      setNewCatName(""); setNewSubName(""); setNewModelName("");
    }
  }, [open]);

  useEffect(() => {
    if (!client) { setDevices([]); return; }
    api.get(`/clients/${client.id}/devices`).then((r) => setDevices(r.data)).catch(() => {});
    setDeviceId(null);
    setNewDevice(false);
  }, [client?.id]);

  const removeDevice = async (id) => {
    if (!window.confirm("Eliminare questo dispositivo dalla scheda del cliente? Le riparazioni già registrate restano nello storico.")) return;
    try {
      await api.delete(`/devices/${id}`);
      setDevices((prev) => prev.filter((d) => d.id !== id));
      if (deviceId === id) setDeviceId(null);
      toast.success("Dispositivo eliminato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const selectedCat = categories.find((c) => c.id === catId);
  const catSubs = normSubs(selectedCat?.subs);
  const selectedBrand = catSubs.find((s) => s.name === sub);

  const selectCat = (name) => {
    const c = categories.find((x) => x.name === name);
    if (!c) return;
    setCatId(c.id); setSub(""); setModelSel(""); setBrand(""); setModel("");
    setAddingSub(false); setAddingModel(false);
  };

  const selectBrand = (name) => {
    setSub(name); setModelSel("");
    setBrand(name); setModel("");
    setAddingModel(false);
  };

  const selectModel = (m) => {
    setModelSel(m); setModel(m);
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const { data } = await api.post("/device-categories", { name: newCatName.trim() });
      setCategories((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setCatId(data.id);
      setSub(""); setModelSel("");
      setNewCatName(""); setAddingCat(false);
      toast.success("Categoria aggiunta");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addSub = async () => {
    if (!newSubName.trim() || !selectedCat) return;
    try {
      const { data } = await api.post(`/device-categories/${selectedCat.id}/subs`, { name: newSubName.trim() });
      setCategories((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      selectBrand(newSubName.trim());
      setNewSubName(""); setAddingSub(false);
      toast.success("Marca aggiunta");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addModel = async () => {
    if (!newModelName.trim() || !selectedCat || !sub) return;
    try {
      const { data } = await api.post(`/device-categories/${selectedCat.id}/subs/${encodeURIComponent(sub)}/models`, { name: newModelName.trim() });
      setCategories((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      selectModel(newModelName.trim());
      setNewModelName(""); setAddingModel(false);
      toast.success("Modello aggiunto");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCategory = async (name) => {
    const c = categories.find((x) => x.name === name);
    if (!c) return;
    if (!window.confirm(`Eliminare la categoria "${name}" con tutte le sue marche e modelli?\nLe pratiche esistenti non vengono toccate.`)) return;
    try {
      await api.delete(`/device-categories/${c.id}`);
      setCategories((prev) => prev.filter((x) => x.id !== c.id));
      if (catId === c.id) { setCatId(null); setSub(""); setModelSel(""); setBrand(""); setModel(""); }
      toast.success("Categoria eliminata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSub = async (name) => {
    if (!selectedCat) return;
    if (!window.confirm(`Eliminare la marca "${name}" e tutti i suoi modelli?\nLe pratiche esistenti non vengono toccate.`)) return;
    try {
      const { data } = await api.delete(`/device-categories/${selectedCat.id}/subs/${encodeURIComponent(name)}`);
      setCategories((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      if (sub === name) { setSub(""); setModelSel(""); setBrand(""); setModel(""); }
      toast.success("Marca eliminata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteModel = async (name) => {
    if (!selectedCat || !sub) return;
    if (!window.confirm(`Eliminare il modello "${name}"?\nLe pratiche esistenti non vengono toccate.`)) return;
    try {
      const { data } = await api.delete(`/device-categories/${selectedCat.id}/subs/${encodeURIComponent(sub)}/models/${encodeURIComponent(name)}`);
      setCategories((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      if (modelSel === name) { setModelSel(""); setModel(""); }
      toast.success("Modello eliminato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAcc = (a) =>
    setAccessories((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const addCustomAcc = () => {
    const v = customAcc.trim();
    if (v && !accessories.includes(v)) setAccessories([...accessories, v]);
    setCustomAcc("");
  };

  const deviceReady = deviceId || (newDevice && catId);
  const canSubmit = client && (deviceId || (newDevice && catId && brand.trim() && model.trim()))
    && problem.trim() && techId && (noPassword || unlockPwd.trim());

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: newRepair } = await api.post("/repairs", {
        client_id: client.id,
        device_id: newDevice ? null : deviceId,
        category: newDevice ? selectedCat?.name : null,
        subcategory: newDevice ? sub : null,
        brand: newDevice ? brand : null,
        model: newDevice ? model : null,
        serial: newDevice ? serial : "",
        accessories,
        problem,
        unlock_password: noPassword ? null : unlockPwd,
        no_password: noPassword,
        technician_id: techId,
        price: price ? parseFloat(String(price).replace(",", ".")) : null,
        estimated_delivery: estDelivery || null,
        notes,
        order_id: initialOrderId || null,
        operator: localStorage.getItem("active_operator") || undefined,
      });
      toast.success("Pratica riparazione aperta");
      setCreated(newRepair);
      onCreated(newRepair);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[96vw] max-h-[94vh] overflow-y-auto" data-testid="repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">Nuova riparazione</DialogTitle>
          <DialogDescription className="sr-only">Modulo di apertura di una nuova pratica di riparazione</DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="text-center py-6 space-y-5" data-testid="repair-created-panel">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-500/15 text-green-500 flex items-center justify-center">
              <CheckCircle size={30} weight="duotone" />
            </div>
            <div>
              <p className="font-heading text-xl font-extrabold">Pratica aperta!</p>
              <p className="text-sm text-muted-foreground mt-1">{created.client_name} — {created.device_label}</p>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cosa vuoi stampare?</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={() => printRepairReceipt(created)} data-testid="print-receipt-created"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
                <Receipt size={18} /> Ricevuta cliente (80mm)
              </button>
              <button onClick={() => printRepairLabel(created)} data-testid="print-label-created"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
                <Barcode size={18} /> Etichetta DYMO (57×32)
              </button>
            </div>
            <button onClick={() => onOpenChange(false)} data-testid="close-created-panel"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
              Chiudi senza stampare
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-8 mt-2 pb-2" data-testid="repair-dialog-form">
          <section>
            <p className={overlineCls}>1 · Cliente</p>
            <ClientSearch selected={client} onSelect={setClient} />
          </section>

          {client && (
            <section>
              <p className={overlineCls}>2 · Dispositivo</p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-3">
                  {devices.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="device-picker">
                      {devices.map((d) => (
                        <div key={d.id} className="flex items-center gap-1.5">
                          <button type="button"
                            onClick={() => { setDeviceId(d.id); setNewDevice(false); }}
                            data-testid={`device-option-${d.id}`}
                            className={`flex-1 text-left px-4 py-2.5 rounded-md border text-sm transition-colors duration-150 flex items-center gap-2.5 ${
                              deviceId === d.id && !newDevice ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
                            <DeviceMobile size={16} weight="duotone" className="text-muted-foreground" />
                            <span className="font-semibold">{d.brand} {d.model}</span>
                            <span className="text-xs text-muted-foreground capitalize ml-auto">{d.type}</span>
                          </button>
                          <button type="button" onClick={() => removeDevice(d.id)}
                            title="Elimina dispositivo (il cliente non lo possiede più)"
                            data-testid={`device-delete-${d.id}`}
                            className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors duration-150 shrink-0">
                            <Trash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button"
                    onClick={() => { setNewDevice(true); setDeviceId(null); }}
                    data-testid="new-device-button"
                    className={`w-full text-left px-4 py-2.5 rounded-md border text-sm font-semibold transition-colors duration-150 ${
                      newDevice ? "border-primary bg-primary/10 text-primary" : "border-dashed border-border text-muted-foreground hover:text-foreground"}`}>
                    + Nuovo dispositivo
                  </button>

                  {newDevice && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="category-cascade">
                      <CascadeColumn
                        title="Categoria"
                        items={categories.map((c) => c.name)}
                        selected={selectedCat?.name || ""}
                        onSelect={selectCat}
                        itemTestid="cat-item"
                        adding={addingCat} setAdding={setAddingCat}
                        newName={newCatName} setNewName={setNewCatName}
                        onAdd={addCategory}
                        addTestid="add-category-cascade-button"
                        inputTestid="new-category-cascade-input"
                        confirmTestid="confirm-category-cascade-button"
                        placeholder="Nessuna categoria"
                        onDelete={deleteCategory}
                      />
                      <CascadeColumn
                        title="Marca"
                        items={catSubs.map((s) => s.name)}
                        selected={sub}
                        onSelect={selectBrand}
                        itemTestid="brand-item"
                        adding={addingSub} setAdding={setAddingSub}
                        newName={newSubName} setNewName={setNewSubName}
                        onAdd={addSub}
                        addTestid="add-brand-cascade-button"
                        inputTestid="new-brand-cascade-input"
                        confirmTestid="confirm-brand-cascade-button"
                        placeholder={selectedCat ? "Nessuna marca" : "Seleziona una categoria"}
                        onDelete={selectedCat ? deleteSub : undefined}
                      />
                      <CascadeColumn
                        title="Modello"
                        items={selectedBrand?.models || []}
                        selected={modelSel}
                        onSelect={selectModel}
                        itemTestid="model-item"
                        adding={addingModel} setAdding={setAddingModel}
                        newName={newModelName} setNewName={setNewModelName}
                        onAdd={addModel}
                        addTestid="add-model-cascade-button"
                        inputTestid="new-model-cascade-input"
                        confirmTestid="confirm-model-cascade-button"
                        placeholder={sub ? "Nessun modello: aggiungilo con +" : "Seleziona una marca"}
                        onDelete={sub ? deleteModel : undefined}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {newDevice && selectedCat && (
                    <>
                      <div className="border border-primary/40 bg-primary/5 rounded-md px-4 py-3" data-testid="device-selection-summary">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Selezionato</p>
                        <p className="font-heading font-bold mt-1">
                          {selectedCat.name}{sub ? ` · ${sub}` : ""}{modelSel ? ` · ${modelSel}` : ""}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Marca *</label>
                          <input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="device-brand-input" className={inputCls} placeholder="Apple, Samsung..." />
                        </div>
                        <div>
                          <label className={labelCls}>Modello *</label>
                          <input value={model} onChange={(e) => setModel(e.target.value)} data-testid="device-model-input" className={inputCls} placeholder="iPhone 13" />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Seriale / IMEI</label>
                        <input value={serial} onChange={(e) => setSerial(e.target.value)} data-testid="device-serial-input" className={inputCls} />
                      </div>
                    </>
                  )}

                  {deviceReady && (
                    <div className="border border-border rounded-md p-4 space-y-3" data-testid="unlock-section">
                      <label className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={noPassword}
                          onChange={(e) => { setNoPassword(e.target.checked); if (e.target.checked) setUnlockPwd(""); }}
                          data-testid="no-password-checkbox"
                          className="w-4 h-4 accent-[#3B82F6]" />
                        <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                          <LockSimpleOpen size={15} /> Dispositivo senza password
                        </span>
                      </label>
                      {!noPassword && (
                        <div>
                          <label className={labelCls}>Password / codice di sblocco *</label>
                          <div className="relative">
                            <LockSimple size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input value={unlockPwd} onChange={(e) => setUnlockPwd(e.target.value)}
                              data-testid="unlock-password-input" className={`${inputCls} pl-10`}
                              placeholder="PIN, sequenza, password..." />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {client && deviceReady && (
            <>
              <section>
                <p className={overlineCls}>3 · Accessori lasciati col dispositivo</p>
                <div className="flex flex-wrap gap-2" data-testid="accessories-picker">
                  {ACCESSORY_SUGGESTIONS.map((a) => (
                    <button key={a} type="button" onClick={() => toggleAcc(a)} data-testid={`accessory-chip-${slug(a)}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        accessories.includes(a)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {a}
                    </button>
                  ))}
                  {accessories.filter((a) => !ACCESSORY_SUGGESTIONS.includes(a)).map((a) => (
                    <button key={a} type="button" onClick={() => toggleAcc(a)} data-testid={`accessory-custom-chip-${slug(a)}`}
                      className="px-3.5 py-2 rounded-full text-xs font-semibold border bg-primary text-primary-foreground border-primary inline-flex items-center gap-1.5">
                      {a} <X size={11} weight="bold" />
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mt-3 max-w-md">
                  <input value={customAcc} onChange={(e) => setCustomAcc(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAcc(); } }}
                    placeholder="Altro accessorio..." data-testid="accessory-custom-input" className={inputCls} />
                  <button type="button" onClick={addCustomAcc} data-testid="add-accessory-button"
                    className="px-4 py-2 rounded-md border border-border text-sm font-semibold shrink-0 hover:bg-accent transition-colors duration-150 inline-flex items-center gap-1.5">
                    <Plus size={14} /> Aggiungi
                  </button>
                </div>
              </section>

              <section>
                <p className={overlineCls}>4 · Dettagli intervento</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Problema riscontrato *</label>
                    <textarea value={problem} onChange={(e) => setProblem(e.target.value)} rows={3}
                      data-testid="repair-problem-input" className={inputCls} placeholder="Es. Schermo rotto, non si accende..." />
                  </div>
                  <div>
                    <label className={labelCls}>Note</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                      data-testid="repair-notes-input" className={inputCls} placeholder="Note interne..." />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className={labelCls}>Tecnico assegnato *</label>
                    <select value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="technician-select" className={inputCls}>
                      {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Prezzo preventivato (€)</label>
                    <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal"
                      data-testid="repair-price-input" className={inputCls} placeholder="0,00" />
                  </div>
                  <div>
                    <label className={labelCls}>Consegna stimata</label>
                    <input type="date" value={estDelivery} onChange={(e) => setEstDelivery(e.target.value)}
                      data-testid="repair-delivery-input" className={inputCls} />
                  </div>
                </div>
              </section>

              <button type="submit" disabled={!canSubmit || saving} data-testid="repair-submit-button"
                className="w-full py-3.5 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-40 transition-opacity duration-150">
                {saving ? "Apertura pratica..." : "Apri pratica"}
              </button>
            </>
          )}
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ repair, open, onOpenChange, onDone }) {
  const [resolution, setResolution] = useState("");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [internalNotes, setInternalNotes] = useState("");

  useEffect(() => {
    if (open && repair) {
      setDone(null);
      setResolution("");
      setInternalNotes(repair.internal_notes || "");
      setCost(repair.price != null ? String(repair.price).replace(".", ",") : "");
    }
  }, [open, repair?.id]);

  const addSuggestion = (s) => setResolution((prev) => (prev.trim() ? `${prev.trim()}; ${s}` : s));

  const submit = async () => {
    if (!resolution.trim()) {
      toast.error("Descrivi il lavoro eseguito");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/repairs/${repair.id}`, {
        status: "completato",
        resolution: resolution.trim(),
        price: cost ? parseFloat(String(cost).replace(",", ".")) : repair.price,
        internal_notes: internalNotes.trim() || null,
      });
      toast.success("Riparazione completata");
      setDone({
        ...repair,
        status: "completato",
        resolution: resolution.trim(),
        price: cost ? parseFloat(String(cost).replace(",", ".")) : repair.price,
      });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Completa riparazione</DialogTitle>
          <DialogDescription className="sr-only">Registra il lavoro eseguito e il costo finale della riparazione</DialogDescription>
        </DialogHeader>
        {repair && !done && (
          <div className="space-y-5 mt-2">
            <div className="border border-border rounded-md px-4 py-3">
              <p className="text-sm font-bold">{repair.client_name} — {repair.device_label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{repair.problem}</p>
            </div>
            <div>
              <label className={labelCls}>Lavoro eseguito *</label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="resolution-suggestions">
                {RESOLUTION_SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => addSuggestion(s)} data-testid={`suggestion-${slug(s)}`}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors duration-150">
                    {s}
                  </button>
                ))}
              </div>
              <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3}
                data-testid="resolution-input" className={inputCls} placeholder="Es. Sostituito display e batteria, testato tutto OK" />
            </div>
            <div>
              <label className={labelCls}>Costo finale (€)</label>
              <input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal"
                data-testid="final-cost-input" className={inputCls} placeholder="0,00" />
            </div>
            <div>
              <label className={labelCls}>Uso interno — lo vedete solo voi (opzionale)</label>
              <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2}
                data-testid="internal-notes-input" className={inputCls}
                placeholder="Note riservate: modifiche, particolari tecnici, cose da ricordare in caso di problemi..." />
            </div>
            <button onClick={submit} disabled={saving} data-testid="confirm-complete-button"
              className="w-full py-3 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-500 disabled:opacity-50 transition-colors duration-150">
              {saving ? "Salvataggio..." : "Conferma completamento"}
            </button>
          </div>
        )}
        {done && (
          <div className="text-center py-4 space-y-5" data-testid="complete-done-panel">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-500/15 text-green-500 flex items-center justify-center">
              <CheckCircle size={30} weight="duotone" />
            </div>
            <div>
              <p className="font-heading text-xl font-extrabold">Riparazione completata!</p>
              <p className="text-sm text-muted-foreground mt-1">{done.client_name} — {done.device_label}</p>
            </div>
            <button onClick={() => { whatsappRepairReady(done); api.patch(`/repairs/${done.id}`, { whatsapp_notified: true }).catch(() => {}); }} data-testid="whatsapp-notify-button"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-500 transition-colors duration-150">
              <WhatsappLogo size={19} weight="fill" /> Avvisa il cliente su WhatsApp
            </button>
            <button onClick={() => printRepairClosure(done)} data-testid="print-closure-created"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
              <Printer size={17} /> Stampa foglio di chiusura (80mm)
            </button>
            <button onClick={() => onOpenChange(false)} data-testid="close-done-panel"
              className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
              Chiudi senza avvisare
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RepairPaymentsDialog({ repair, open, onOpenChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (open && repair) {
      setItems(null);
      api.get(`/repairs/${repair.id}/payments`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }
  }, [open, repair]);

  const methodLabel = (m) => ({ contanti: "Contanti", carta: "Carta", bonifico: "Bonifico", assegno: "Assegno", cash: "Cash", finanziamento: "Finanziamento", bonus_docenti: "Bonus Docenti" }[m] || m);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="repair-payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Pagamenti in Prima Nota</DialogTitle>
          <DialogDescription className="sr-only">Dettaglio dei pagamenti registrati in prima nota per questa riparazione</DialogDescription>
        </DialogHeader>
        {repair && (
          <div className="space-y-4 mt-2">
            <div className="border border-border rounded-md px-4 py-3">
              <p className="text-sm font-bold">{repair.client_name} — {repair.device_label}</p>
            </div>
            {items === null && <p className="text-sm text-muted-foreground">Caricamento...</p>}
            {items !== null && items.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="repair-payments-empty">Nessun movimento trovato in Prima Nota per questa riparazione.</p>
            )}
            {(items || []).map((e) => (
              <div key={e.id} className="border border-border rounded-md px-4 py-3 space-y-1" data-testid={`repair-payment-row-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">Riparazione</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(e.date)}</span>
                </div>
                {(e.payments || []).map((p, i) => (
                  <p key={i} className="text-sm">{methodLabel(p.method)}: <b>€ {Number(p.amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b></p>
                ))}
                {e.balance_due > 0 && (
                  <p className="text-sm text-amber-500">Residuo da incassare: <b>€ {Number(e.balance_due).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b></p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {e.receipt_type === "fattura" ? "Fattura" : e.receipt_type === "scontrino" ? "Scontrino" : "Non contabilizzato (cash)"}{e.invoice_number ? ` n. ${e.invoice_number}` : ""} · inserito da {e.created_by}
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ repair, open, onOpenChange, onDone }) {
  const [method, setMethod] = useState("contanti");
  const [receipt, setReceipt] = useState("scontrino");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [paid, setPaid] = useState("pagato");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMethod("contanti"); setReceipt("scontrino"); setInvoiceNumber(""); setPaid("pagato");
      setAmount(repair?.price != null ? String(repair.price).replace(".", ",") : "");
    }
  }, [open, repair]);

  const unpaidSel = receipt === "fattura" && paid === "da_pagare";
  const parsedAmount = parseFloat(String(amount).replace(",", ".")) || 0;

  const submit = async () => {
    if (!(parsedAmount > 0)) {
      toast.error("Inserisci un importo valido");
      return;
    }
    if (method !== "cash" && receipt === "fattura" && !invoiceNumber.trim()) {
      toast.error("Inserisci il numero della fattura");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/repairs/${repair.id}/register-payment`, {
        method: unpaidSel ? undefined : method,
        receipt_type: method === "cash" ? undefined : receipt,
        invoice_number: method !== "cash" && receipt === "fattura" ? invoiceNumber.trim() : undefined,
        unpaid: unpaidSel || undefined,
        amount: parsedAmount,
        operator: localStorage.getItem("active_operator") || undefined,
      });
      toast.success(unpaidSel ? "Fattura registrata come da incassare" : "Pagamento registrato in Prima Nota");
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Registra in Prima Nota</DialogTitle>
          <DialogDescription className="sr-only">Registra il pagamento della riparazione completata in prima nota</DialogDescription>
        </DialogHeader>
        {repair && (
          <div className="space-y-5 mt-2">
            <div className="border border-border rounded-md px-4 py-3">
              <p className="text-sm font-bold">{repair.client_name} — {repair.device_label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{repair.resolution || repair.problem}</p>
              <div className="mt-2">
                <label className={labelCls}>Importo da registrare (€) — modificabile in caso di imprevisti o sconti</label>
                <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
                  data-testid="payment-amount-input"
                  className="w-full px-3 py-2 rounded-md bg-background border border-input font-heading text-2xl font-extrabold focus:outline-none focus:border-primary transition-colors duration-150" />
                {repair.price != null && parsedAmount > 0 && Math.abs(parsedAmount - Number(repair.price)) > 0.001 && (
                  <p className="text-xs text-amber-500 mt-1.5" data-testid="payment-amount-changed">
                    Prezzo concordato: € {Number(repair.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })} — verrà aggiornato anche sulla pratica
                  </p>
                )}
              </div>
            </div>
            <div className={unpaidSel ? "opacity-50 pointer-events-none" : ""}>
              <label className={labelCls}>Metodo di pagamento</label>
              <div className="flex flex-wrap gap-2">
                {[["contanti", "Contanti"], ["carta", "Carta"], ["bonifico", "Bonifico"], ["assegno", "Assegno"], ["bonus_docenti", "Bonus Docenti"], ["finanziamento", "Finanziamento"], ["cash", "Cash"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`payment-method-${v}`}
                    className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                      method === v
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Documento</label>
              <div className="flex flex-wrap gap-2">
                {[["scontrino", "Scontrino"], ["fattura", "Fattura"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => method !== "cash" && setReceipt(v)} disabled={method === "cash"} data-testid={`payment-receipt-${v}`}
                    className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                      method === "cash"
                        ? "border-border text-muted-foreground/40 opacity-50 cursor-not-allowed"
                        : receipt === v
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {method !== "cash" && receipt === "fattura" && (
              <div>
                <label className={labelCls}>Numero fattura *</label>
                <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
                  inputMode="text" data-testid="invoice-number-input" className={inputCls} placeholder="Es. 12/2026-A" />
              </div>
            )}
            {method !== "cash" && receipt === "fattura" && (
              <div>
                <label className={labelCls}>Incasso</label>
                <div className="flex flex-wrap gap-2" data-testid="payment-paid-toggle">
                  {[["pagato", "Pagato"], ["da_pagare", "Da pagare"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setPaid(v)} data-testid={`payment-paid-${v}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                        paid === v
                          ? v === "da_pagare"
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-green-600 text-white border-green-600"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {unpaidSel && (
                  <p className="text-xs text-amber-500 mt-2" data-testid="unpaid-hint">
                    La fattura viene annotata subito ma l'importo resta <b>da incassare</b> (sezione Crediti in Prima Nota): non gonfia gli incassi.
                  </p>
                )}
              </div>
            )}
            <button onClick={submit} disabled={saving || !(parsedAmount > 0) || (receipt === "fattura" && !invoiceNumber.trim())} data-testid="confirm-payment-button"
              className="w-full py-3 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-500 disabled:opacity-50 transition-colors duration-150">
              {saving ? "Registrazione..." : unpaidSel ? "Registra fattura da incassare" : "Registra pagamento"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Riparazioni() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const openHandled = useRef(false);
  useEffect(() => {
    const target = searchParams.get("apri");
    if (target && !openHandled.current && repairs.length > 0) {
      openHandled.current = true;
      setExpandedId(target);
      setTimeout(() => {
        document.querySelector(`[data-testid="repair-card-${target}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [searchParams, repairs]);
  const [filter, setFilter] = useState("in_lavorazione");
  const [expandedId, setExpandedId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [techFilter, setTechFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [initialClient, setInitialClient] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [paying, setPaying] = useState(null);
  const [payInfo, setPayInfo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const removeRepair = async (r) => {
    if (!window.confirm(`Eliminare definitivamente la pratica di ${r.client_name} (${r.device_label})?\nL'operazione non si può annullare.`)) return;
    try {
      await api.delete(`/repairs/${r.id}`);
      toast.success("Pratica eliminata");
      loadRepairs(page).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadRepairs = async (p = 1) => {
    const params = { skip: (p - 1) * PAGE_SIZE, limit: PAGE_SIZE };
    if (filter) params.status = filter;
    if (techFilter) params.technician_id = techFilter;
    if (q) params.q = q;
    params.sort = sortBy;
    const countParams = {};
    if (filter) countParams.status = filter;
    if (techFilter) countParams.technician_id = techFilter;
    if (q) countParams.q = q;
    const [{ data: items }, { data: c }] = await Promise.all([
      api.get("/repairs", { params }),
      api.get("/repairs/count", { params: countParams }),
    ]);
    setRepairs(items);
    setTotal(c.count);
    setPage(p);
  };

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { loadRepairs(1).catch(() => {}); }, [filter, techFilter, q, sortBy]);

  useEffect(() => {
    setSortBy((s) => (filter === "completato" || filter === "archivio" ? (s === "date" ? "chiusura" : s) : (s === "chiusura" ? "date" : s)));
  }, [filter]);
  useEffect(() => {
    api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const cid = searchParams.get("cliente");
    if (cid) {
      api.get(`/clients/${cid}`).then((r) => {
        setInitialClient({ id: r.data.id, name: r.data.name, phone: r.data.phone });
        setModalOpen(true);
      }).catch(() => {});
    }
    if (searchParams.get("nuova")) {
      setModalOpen(true);
    }
  }, [searchParams]);

  const notifyWhatsApp = async (r) => {
    whatsappRepairReady(r);
    try {
      await api.patch(`/repairs/${r.id}`, { whatsapp_notified: true });
      setRepairs((prev) => prev.map((x) => (x.id === r.id ? { ...x, whatsapp_notified_at: new Date().toISOString() } : x)));
    } catch { /* ignore */ }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/repairs/${id}`, { status });
      toast.success("Riparazione riaperta");
      await loadRepairs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPickedUp = async (r, value = true) => {
    try {
      await api.patch(`/repairs/${r.id}`, { picked_up: value });
      toast.success(value ? "Pratica spostata in Archivio" : "Pratica riportata tra i Completati");
      await loadRepairs(page);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageNumbers = [];
  for (let n = Math.max(1, page - 2); n <= Math.min(totalPages, page + 2); n++) pageNumbers.push(n);

  return (
    <div className="p-8 space-y-8" data-testid="riparazioni-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Pratiche di riparazione</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Riparazioni</h1>
        </div>
        <button onClick={() => setModalOpen(true)} data-testid="open-new-repair-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
          <Plus size={17} weight="bold" /> Nuova riparazione
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2 flex-wrap items-center">
          {[["in_lavorazione", "In lavorazione"], ["completato", "Da consegnare"], ["archivio", "Chiuse e ritirate"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v || "tutti"}-button`}
              className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors duration-150 ${
                filter === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
          <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="repairs-tech-filter"
            className="px-3 py-2 rounded-md bg-background border border-input text-sm font-semibold focus:outline-none focus:border-primary transition-colors duration-150">
            <option value="">Tutti i tecnici</option>
            {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="repairs-sort-select"
            className="px-3.5 py-2.5 rounded-md bg-background border border-border text-sm focus:outline-none focus:border-primary transition-colors duration-150">
            <option value="date">Ordina: data ricezione</option>
            <option value="chiusura">Ordina: data chiusura</option>
            <option value="alpha">Ordina: alfabetico</option>
          </select>
          <div className="relative flex-1 min-w-[240px] max-w-xl">
            <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="repairs-search-input"
              placeholder="Cerca riparazione: scrivi qualsiasi cosa (nome, cognome, telefono, codice...)"
              className="w-full pl-10 pr-9 py-2.5 rounded-md bg-background border border-primary/50 text-sm focus:outline-none focus:border-primary transition-colors duration-150" />
            {search && (
              <button type="button" onClick={() => setSearch("")} title="Cancella la ricerca" data-testid="repairs-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>

        <div className="border border-border rounded-md bg-card" data-testid="repairs-list">
          {repairs.length === 0 && (
            <div className="p-10 text-center text-muted-foreground text-sm" data-testid="repairs-empty">Nessuna riparazione trovata</div>
          )}
          <div className={`hidden md:grid items-center gap-x-6 px-4 py-2 border-b border-border text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold ${filter === "in_lavorazione" ? "md:grid-cols-[14px_10px_200px_minmax(0,1fr)_108px_80px_210px]" : "md:grid-cols-[14px_10px_200px_minmax(0,1fr)_108px_80px_350px]"}`} data-testid="repairs-header">
            <span></span><span></span>
            <span>Cliente</span>
            <span>Dispositivo · Problema</span>
            <span>Codice</span>
            <span className="text-right">Prezzo</span>
            <span className="text-right">Date · Azioni</span>
          </div>
          <div className="divide-y divide-border">
            {repairs.map((r) => { const dev = deviceDisplay(r); return (
              <div key={r.id}
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                className={`px-3 py-2 sm:px-4 sm:py-2.5 cursor-pointer transition-colors duration-150 hover:bg-accent/40 ${expandedId === r.id ? "bg-accent/30" : ""}`}
                data-testid={`repair-card-${r.id}`}>
                <div className={`grid grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-2.5 md:gap-x-6 [grid-template-areas:'a_b_c'_'a_b_d'_'f_f_f'] md:[grid-template-areas:'a_b_c_d_g_h_f'] ${filter === "in_lavorazione" ? "md:grid-cols-[14px_10px_200px_minmax(0,1fr)_108px_80px_210px]" : "md:grid-cols-[14px_10px_200px_minmax(0,1fr)_108px_80px_350px]"}`}>
                  <CaretRight size={14} className={`[grid-area:a] self-center shrink-0 text-muted-foreground transition-transform duration-150 ${expandedId === r.id ? "rotate-90" : ""}`} />
                  <span data-testid={`repair-status-${r.id}`}
                    title={r.status === "in_lavorazione" ? "In lavorazione" : r.picked_up ? "Conclusa e ritirata" : "Completato, da consegnare"}
                    className={`[grid-area:b] self-center justify-self-center w-2.5 h-2.5 rounded-full shadow-[0_0_8px_currentColor] ${
                      r.status === "in_lavorazione" ? "bg-amber-500 text-amber-500" : r.picked_up ? "bg-green-500 text-green-500" : "bg-blue-500 text-blue-500"}`} />
                  <div className="[grid-area:c] min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-heading font-extrabold text-[14px] md:text-[15px] truncate">{r.client_name}</span>
                      {r.price != null && (
                        <span className="md:hidden shrink-0 text-[11px] font-extrabold bg-secondary px-2 py-0.5 rounded-md border border-border">
                          € {Number(r.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[11px] font-mono text-muted-foreground">Tel: {r.client_phone}</span>
                      <span className="md:hidden inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary border border-border text-[10px] text-muted-foreground">
                        {r.no_password ? <><LockSimpleOpen size={11} /> Senza password</> : <><LockSimple size={11} /> Codice: <b className="text-foreground">{r.unlock_password}</b></>}
                      </span>
                    </div>
                  </div>
                  <div className="[grid-area:d] min-w-0 mt-0.5 md:mt-0">
                    <div className="flex items-baseline gap-2 min-w-0 text-[11px] md:text-[12.5px]">
                      <span className={`text-primary truncate ${filter === "completato" ? "font-extrabold text-[13px] md:text-sm" : "font-bold md:shrink-0 md:max-w-[45%]"}`}>
                        {dev.label}{dev.sub}
                        {dev.type && <span className="text-muted-foreground capitalize font-normal">{dev.type}</span>}
                      </span>
                      {r.problem && filter !== "completato" && (
                        <span className="text-muted-foreground text-[10px] md:text-[11px] truncate" data-testid={`repair-problem-${r.id}`}>· {r.problem}</span>
                      )}
                    </div>
                    {(r.picked_up || r.order_id) && (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10px] text-muted-foreground" data-testid={`repair-meta-${r.id}`}>
                        {r.picked_up && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-500/10 border border-green-500/30 text-green-600 font-bold" data-testid={`archived-label-${r.id}`}>
                            <CheckCircle size={12} weight="fill" /> Riparaz. conclusa e ritirata{r.picked_up_at ? ` il ${fmtDate(r.picked_up_at)}` : ""}
                          </span>
                        )}
                        {r.order_id && (
                          <Link to={`/ordini?apri=${r.order_id}`} onClick={(e) => e.stopPropagation()} data-testid={`from-order-${r.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/10 border border-violet-500/30 text-violet-400 font-bold hover:opacity-80 transition-opacity duration-150">
                            <Package size={12} /> Da ordine ricambio
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="[grid-area:g] hidden md:block text-right pl-3">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary border border-border text-[10px] text-muted-foreground whitespace-nowrap">
                      {r.no_password ? <><LockSimpleOpen size={11} /> Senza password</> : <><LockSimple size={11} /> Codice: <b className="text-foreground">{r.unlock_password}</b></>}
                    </span>
                  </div>
                  <div className="[grid-area:h] hidden md:block text-right pl-3">
                    {r.price != null && (
                      <span className="text-xs font-extrabold bg-secondary px-2 py-0.5 rounded-md border border-border whitespace-nowrap" data-testid={`repair-price-tag-${r.id}`}>
                        € {Number(r.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                  <div className="[grid-area:f] mt-1.5 md:mt-0 flex flex-col gap-2 md:items-end md:self-center">
                    <div className="flex items-center gap-3 flex-wrap md:justify-end">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground mr-0.5" title="Data di ricezione" data-testid={`repair-received-${r.id}`}>
                      <CalendarPlus size={12} className="shrink-0" /> {fmtDate(r.created_at)}{r.status === "completato" && r.completed_at && (
                        <span className="text-green-500 inline-flex items-center gap-1" title="Data di chiusura" data-testid={`repair-closed-${r.id}`}> · <CalendarCheck size={12} /> {fmtDate(r.completed_at)}</span>
                      )}
                    </span>
                    {r.whatsapp_notified_at && (
                      <span className="inline-flex items-center gap-0.5 text-green-500 text-[10px] font-mono" title={`Cliente avvisato su WhatsApp il ${fmtDate(r.whatsapp_notified_at)}`} data-testid={`whatsapp-notified-${r.id}`}>
                        <WhatsappLogo size={12} weight="fill" /> {fmtDate(r.whatsapp_notified_at)}
                      </span>
                    )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap md:justify-end">
                    {r.status === "completato" && !r.picked_up && (r.cash_registered ? (
                      <button onClick={(e) => { e.stopPropagation(); setPayInfo(r); }} title="Pagamento registrato in Prima Nota — vedi dettaglio" data-testid={`cash-registered-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-green-500/15 text-green-500 text-[9px] font-bold uppercase tracking-wider hover:bg-green-500/25 transition-colors duration-150">
                        <CheckCircle size={12} weight="fill" /> In prima nota
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setPaying(r); }} title="Registra il pagamento in Prima Nota" data-testid={`register-payment-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-amber-500/50 text-amber-500 text-[9px] font-bold uppercase tracking-wider hover:bg-amber-500/10 transition-colors duration-150">
                        <CurrencyEur size={12} weight="bold" /> Prima nota
                      </button>
                    ))}
                    {r.picked_up && r.cash_registered && (r.payment_pending ? (
                      <button onClick={(e) => { e.stopPropagation(); setPayInfo(r); }} title="Vedi il pagamento registrato in Prima Nota" data-testid={`payment-pending-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-500 text-[9px] font-bold uppercase tracking-wider hover:bg-amber-500/25 transition-colors duration-150">
                        <CurrencyEur size={12} weight="bold" /> Da incassare
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setPayInfo(r); }} title="Vedi il pagamento registrato in Prima Nota" data-testid={`cash-registered-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-green-500/15 border border-green-500/30 text-green-500 text-[9px] font-bold uppercase tracking-wider hover:bg-green-500/25 transition-colors duration-150">
                        <CheckCircle size={12} weight="fill" /> In prima nota
                      </button>
                    ))}
                    {r.picked_up && !r.cash_registered && (
                      <span data-testid={`not-in-primanota-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-secondary text-muted-foreground text-[9px] font-bold uppercase tracking-wider">
                        <CurrencyEur size={12} weight="bold" /> Non in prima nota
                      </span>
                    )}
                    {r.status === "in_lavorazione" ? (
                      <button onClick={(e) => { e.stopPropagation(); whatsappContactClient(r); }} title="Contatta il cliente su WhatsApp"
                        data-testid={`whatsapp-contact-${r.id}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-green-500/50 text-green-500 hover:bg-green-500/10 transition-colors duration-150 shrink-0">
                        <WhatsappLogo size={14} weight="fill" />
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); notifyWhatsApp(r); }} title="Avvisa il cliente su WhatsApp"
                        data-testid={`whatsapp-${r.id}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-green-500/50 text-green-500 hover:bg-green-500/10 transition-colors duration-150 shrink-0">
                        <WhatsappLogo size={14} weight="fill" />
                      </button>
                    )}
                    {r.status === "in_lavorazione" ? (
                      <button onClick={(e) => { e.stopPropagation(); printRepairReceipt(r); }} title="Stampa ricevuta cliente (80mm)"
                        data-testid={`print-receipt-${r.id}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150 shrink-0">
                        <Receipt size={14} />
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); printRepairClosure(r); }} title="Stampa foglio di chiusura (80mm)"
                        data-testid={`print-closure-${r.id}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150 shrink-0">
                        <Printer size={14} />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); printRepairLabel(r); }} title="Stampa etichetta DYMO (57×32mm)"
                      data-testid={`print-label-${r.id}`}
                      className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150 shrink-0">
                      <Barcode size={14} />
                    </button>
                    {r.status === "in_lavorazione" ? (
                      <button onClick={(e) => { e.stopPropagation(); setCompleting(r); }} data-testid={`complete-repair-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-green-500/15 border border-green-500/40 text-green-500 text-[10px] font-bold hover:bg-green-500/25 transition-colors duration-150">
                        <CheckCircle size={13} /> Completa
                      </button>
                    ) : !r.picked_up && (
                      <button onClick={(e) => { e.stopPropagation(); markPickedUp(r); }} title="Segna come ritirato: sposta la pratica in Archivio"
                        data-testid={`pickup-repair-${r.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-green-500/50 text-green-500 text-[10px] font-bold uppercase tracking-wider hover:bg-green-500/10 transition-colors duration-150">
                        <Archive size={12} weight="bold" /> Ritirato
                      </button>
                    )}
                    {r.status === "completato" && (
                    <div className="relative">
                      <button onClick={(e) => { e.stopPropagation(); setMenuId(menuId === r.id ? null : r.id); }} title="Altre azioni"
                        data-testid={`repair-menu-${r.id}`}
                        className="w-7 h-7 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                        <DotsThree size={16} weight="bold" />
                      </button>
                      {menuId === r.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMenuId(null); }} />
                          <div className="absolute right-0 top-full mt-1 z-50 min-w-[190px] rounded-md border border-border bg-secondary shadow-xl py-1" data-testid={`repair-menu-panel-${r.id}`}>
                            <button onClick={(e) => { e.stopPropagation(); setMenuId(null); setStatus(r.id, "in_lavorazione"); }}
                              data-testid={`reopen-repair-${r.id}`}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                              <ArrowCounterClockwise size={14} /> Riapri pratica
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    )}
                    </div>
                  </div>
                </div>
                {expandedId === r.id && (
                  <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground space-y-1.5" data-testid={`repair-expanded-${r.id}`}>
                    <p>Problema: <span className="text-foreground font-bold text-[13px]">{r.problem}</span></p>
                    <p data-testid={`repair-technician-${r.id}`}>Tecnico: <span className="text-foreground">{r.technician_name}</span></p>
                    {r.resolution && <p data-testid={`repair-resolution-${r.id}`}>Intervento eseguito: <span className="text-foreground font-bold text-[13px]">{r.resolution}</span></p>}
                    {(r.accessories || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`repair-accessories-${r.id}`}>
                        {r.accessories.map((a) => (
                          <span key={a} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-[10px] font-semibold">{a}</span>
                        ))}
                      </div>
                    )}
                    {r.internal_notes && (
                      <p className="px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-500" data-testid={`repair-internal-${r.id}`}>
                        <b>Uso interno:</b> {r.internal_notes}
                      </p>
                    )}
                    {r.notes && <p>Note: <span className="text-foreground">{r.notes}</span></p>}
                    {r.subcategory && <p>Sottocategoria: <span className="text-foreground">{r.subcategory}</span></p>}
                    {r.estimated_delivery && <p>Consegna stimata: <span className="text-foreground">{r.estimated_delivery}</span></p>}
                    <p>Registrata da: <span className="text-foreground">{r.created_by}</span>{r.completed_at ? ` · Completata il ${fmtDate(r.completed_at)}` : ""}</p>
                    {r.price != null && <p>Prezzo: <span className="text-foreground">€ {Number(r.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span></p>}
                    <div className="pt-2 flex items-center gap-2 flex-wrap">
                      {r.picked_up && (
                        <button onClick={(e) => { e.stopPropagation(); markPickedUp(r, false); }} data-testid={`unarchive-repair-${r.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-muted-foreground text-xs font-bold hover:text-foreground hover:bg-accent transition-colors duration-150">
                          <ArrowCounterClockwise size={14} /> Riporta tra i Completati
                        </button>
                      )}
                      {r.status === "in_lavorazione" && (
                        <button onClick={(e) => { e.stopPropagation(); setEditing(r); }} data-testid={`edit-repair-${r.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/50 text-primary text-xs font-bold hover:bg-primary/10 transition-colors duration-150">
                          <PencilSimple size={14} /> Modifica pratica
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button onClick={(e) => { e.stopPropagation(); removeRepair(r); }} data-testid={`delete-repair-${r.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-500/40 text-red-500 text-xs font-bold hover:bg-red-500/10 transition-colors duration-150">
                          <Trash size={14} /> Elimina pratica
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ); })}
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-border" data-testid="repairs-pagination">
              <p className="text-xs text-muted-foreground" data-testid="repairs-page-info">
                Pagina {page} di {totalPages} — {total} riparazioni
              </p>
              <div className="flex items-center gap-1.5">
                <button disabled={page <= 1} onClick={() => loadRepairs(page - 1).catch(() => {})} data-testid="repairs-page-prev"
                  className="w-8 h-8 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 transition-colors duration-150">
                  <CaretLeft size={14} />
                </button>
                {pageNumbers.map((n) => (
                  <button key={n} onClick={() => loadRepairs(n).catch(() => {})} data-testid={`repairs-page-${n}`}
                    className={`w-8 h-8 rounded-md border text-xs font-bold transition-colors duration-150 ${
                      n === page ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {n}
                  </button>
                ))}
                <button disabled={page >= totalPages} onClick={() => loadRepairs(page + 1).catch(() => {})} data-testid="repairs-page-next"
                  className="w-8 h-8 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 transition-colors duration-150">
                  <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <RepairDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreated={() => loadRepairs(page)}
        technicians={technicians}
        initialClient={initialClient}
      />
      <CompleteDialog
        repair={completing}
        open={!!completing}
        onOpenChange={(v) => !v && setCompleting(null)}
        onDone={() => loadRepairs(page)}
      />
      <PaymentDialog
        repair={paying}
        open={!!paying}
        onOpenChange={(v) => !v && setPaying(null)}
        onDone={() => loadRepairs(page)}
      />
      <RepairPaymentsDialog
        repair={payInfo}
        open={!!payInfo}
        onOpenChange={(v) => !v && setPayInfo(null)}
      />
      <EditRepairDialog
        repair={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        technicians={technicians}
        onSaved={() => loadRepairs(page)}
      />
    </div>
  );
}
